import React, { useMemo, useCallback, useState } from "react"
import { Accordion, Nav, Spinner, Button, Form, Row, Col } from "react-bootstrap"
import { Link, useLocation } from "react-router-dom"
import { useCalendar } from "../../context/CalendarContext"
import { useAuth } from "../../context/AuthContext"
import { toast } from "react-toastify"
import DateTimePicker from "../common/DateTimePicker"

// Status color mapping
const getStatusClass = status => {
  switch (status) {
    case 0: // Unassigned
      return "status-unassigned"
    case 1: // Pending
      return "status-pending"
    case 2: // Pending
      return "status-eexpert"
    case 10: // Completed
      return "status-completed"
    case 99: // Rejected
      return "status-rejected"
    default:
      return ""
  }
}

const CalendarView = () => {
  const location = useLocation()
  const { inspections, isLoading: initialLoading, error, refresh, dateRange, setDateRange } = useCalendar()
  const { isAdmin } = useAuth()
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Handle date change
  const handleDateChange = (field, value) => {
    const newDate = value ? new Date(value) : null
    if (field === "from" && newDate) {
      newDate.setHours(0, 0, 0, 0)
    } else if (field === "to" && newDate) {
      newDate.setHours(23, 59, 59, 999)
    }

    setDateRange(prev => ({
      ...prev,
      [field]: newDate
    }))
  }

  // Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await refresh()
    } catch (error) {
      toast.error("Αποτυχία ανανέωσης ελέγχων")
    } finally {
      setIsRefreshing(false)
    }
  }

  // Format date in Greek style
  const formatDateInGreek = useCallback(dateString => {
    if (!dateString) return ""
    const date = new Date(dateString)
    return date.toLocaleDateString("el-GR", {
      weekday: "long",
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    })
  }, [])

  // Group inspections by ExpertAssignmentDate
  const groupedInspections = useMemo(() => {
    if (!inspections || inspections.length === 0) return []

    const groups = {}

    inspections.forEach(inspection => {
      if (!inspection.ExpertAssignmentDate) return

      const date = new Date(inspection.ExpertAssignmentDate).toLocaleDateString()
      if (!groups[date]) {
        groups[date] = []
      }
      groups[date].push(inspection)
    })

    // Sort dates in ascending order
    return Object.entries(groups).sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
  }, [inspections])

  return (
    <div className="p-3 pt-0">
      <div className="content-glass mb-3">
        <div className="p-3 calendar-wrapper">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Ημερολόγιο Αναθέσεων</h5>
            <Button
              variant="link"
              size="sm"
              className="text-primary p-2"
              onClick={handleRefresh}
              disabled={initialLoading || isRefreshing}>
              {initialLoading || isRefreshing ? (
                <Spinner animation="border" size="lg" />
              ) : (
                <i style={{ fontSize: "2rem" }} className="fas fa-sync-alt" />
              )}
            </Button>
          </div>

          <Row className="g-2">
            <Col xs={12}>
              <Form.Group>
                <DateTimePicker
                  placeholder="Από..."
                  value={dateRange.from}
                  onChange={value => handleDateChange("from", value)}
                  disabled={initialLoading || isRefreshing}
                  enableTime={false}
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <DateTimePicker
                  placeholder="Έως..."
                  value={dateRange.to}
                  onChange={value => handleDateChange("to", value)}
                  disabled={initialLoading || isRefreshing}
                  enableTime={false}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      </div>

      {!error && (
        <>
          {!(initialLoading || isRefreshing) && groupedInspections.length > 0 && (
            <Accordion alwaysOpen className="calendar-accordion">
              {groupedInspections.map(([date, dateInspections]) => (
                <Accordion.Item key={date} eventKey={date}>
                  <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-center w-100">
                      <span>{formatDateInGreek(date)}</span>
                      {dateInspections.filter(inspection => inspection.Status === 1).length > 0 && (
                        <span className="badge bg-warning rounded-pill ms-2">
                          {dateInspections.filter(inspection => inspection.Status === 1).length}
                        </span>
                      )}
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Nav className="flex-column">
                      {dateInspections.map(inspection => (
                        <Nav.Item key={inspection.InspectionID}>
                          <Nav.Link
                            as={Link}
                            to={`/inspections/${inspection.InspectionID}`}
                            className={`py-2 px-3 border-bottom ${
                              location.pathname === `/inspections/${inspection.InspectionID}` ? "active" : ""
                            } ${getStatusClass(inspection.Status)}`}>
                            <div className="d-flex align-items-center">
                              <small className="text-muted me-2">
                                {new Date(inspection?.ExpertAssignmentDate).toLocaleTimeString("el-GR", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: false
                                })}
                              </small>
                              <small className="text-muted me-2">#{inspection.InspectionID}</small>
                              <small className="text-muted">
                                {inspection?.PlateNumber}
                                {isAdmin() && inspection?.ExpertID && (
                                  <span className="ms-1">({inspection.ExpertID})</span>
                                )}
                              </small>
                            </div>
                          </Nav.Link>
                        </Nav.Item>
                      ))}
                    </Nav>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
        </>
      )}
    </div>
  )
}

export default CalendarView
